<template>
    <div class="licence__show">
        <div class="licence__show_header">
            <view-actions
                :title="`Licence consumption details`"
                :cancel-button-active="false"
                :submit-button-active="true"
                :submit-button-text="'Export all to CSV'"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-event-name="'generate-licence-consumptions-csv'"
                :breadcrumb-active="true"
                :breadcrumb-items="[
                    {
                        name: 'Licences',
                        to: { name: 'ebs-licence-list' }
                    }
                ]"
                @emit-notify-cancel-submit="licenceStopShow"
                @emit-submit="handleGenerateLicenceConsumptionsCSV"
            />
            <a
                class="licence__show_hiddenlink hidden"
                id="download-licence-consumptions-csv"
            ></a>
        </div>
        <div class="licence__show_main">
            <div class="container">
                <component-wrapper :class="'licence__show_main_details'">
                    <div class="columns is-variable">
                        <div class="column">
                            <div class="licence__show_main_details_row">
                                <div class="columns is-vcentered">
                                    <div class="column is-narrow">
                                        <component-title :size="'small'">Status:</component-title>
                                    </div>
                                    <div class="column is-narrow">
                                        <component-status :status="licenceStatus"></component-status>
                                    </div>
                                </div>
                            </div>
                            <template v-if="licenceLatestStateHistoryDescription">
                                <div class="licence__show_main_details_row">
                                    <div class="columns is-vcentered">
                                        <div class="column is-narrow">
                                            <component-title :size="'small'">Description:</component-title>
                                        </div>
                                        <div class="column is-narrow">
                                            <component-text :html="true">{{ licenceLatestStateHistoryDescription }}</component-text>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div class="licence__show_main_details_row">
                                <div class="columns is-vcentered">
                                    <div class="column is-narrow">
                                        <component-title :size="'small'">Course:</component-title>
                                    </div>
                                    <div class="column is-narrow">
                                        <component-text>{{ routeQueryCourseName }}</component-text>
                                    </div>
                                </div>
                            </div>
                            <div class="licence__show_main_details_row">
                                <div class="columns is-vcentered">
                                    <div class="column is-narrow">
                                        <component-title :size="'small'">Customer:</component-title>
                                    </div>
                                    <div class="column is-narrow">
                                        <component-text>{{ routeQueryCustomerName }}</component-text>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="column is-narrow">
                            <div class="columns is-variable">
                                <template v-if="licenceStatus === 'suspended'">
                                    <div class="column is-narrow">
                                        <component-button
                                            :name="'licence-show-main-details-restore'"
                                            :theme="'default'"
                                            @click="openNotifyRestoreLicence"
                                        >Restore
                                        </component-button>
                                    </div>
                                    <div class="column is-narrow">
                                        <component-button
                                            :name="'licence-show-main-details-archive'"
                                            :theme="'default'"
                                            @click="openNotifyArchiveLicence"
                                        >Archive
                                        </component-button>
                                    </div>
                                </template>
                            </div>
                            <template v-if="licenceStatus === 'accepted'">
                                <div class="column is-narrow">
                                    <component-button
                                        :name="'licence-show-main-details-suspend'"
                                        :theme="'default'"
                                        @click="openNotifySuspendLicence"
                                    >Suspend
                                    </component-button>
                                </div>
                            </template>
                        </div>
                    </div>
                </component-wrapper>
            </div>
            <component-list
                ref="componentlist"
                :component-table="LicenceShowTable"
                :component-table-name="'licences'"
                :filters-active="false"
                :filter-select-active="false"
                :get-pagination-getter="'getEbsLicenceConsumptionsPagination'"
                :get-items-getter="'getEbsLicenceConsumptionsData'"
                :get-items-dispatch="'ebsLicenceConsumptionsGet'"
                :get-items-dispatch-data="routeParamsContractId"
                :empty-state-title="'Details about licence consumption will appear here.'"
                :item-name="['licence', 'licences']"
                :table-preferences-active="true"
                @emit-collapse="handleCollapse"
            />
        </div>
        <pop-notify
            ref="notifysuspendoffer"
            :class="'pop__notify--suspend'"
            :title-text="'Suspend offer?'"
            :content-text="'Suspending this offer will prevent the organisation from accessing this course.\n Please give a reason for why you are suspending the offer.'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Confirm'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            :size="'medium'"
            @emit-submit="performSuspendLicence"
        >
            <form-editor
                :name="'licence-list-table-notify-note'"
                v-model="licenceDescription"
            />
        </pop-notify>
        <pop-notify
            ref="notifyrestoreoffer"
            :class="'pop__notify--restore'"
            :title-text="'Restore offer?'"
            :content-text="'Restoring this offer will give the organisation access to this course again.\n Please give a reason for why you are restoring the offer.'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Ok'"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            :size="'medium'"
            @emit-submit="performRestoreLicence"
        >
            <form-editor
                :name="'licence-list-table-notify-note-restore'"
                v-model="licenceDescription"
            />
        </pop-notify>
        <pop-notify
            ref="notifyarchiveoffer"
            :class="'pop__notify--archive'"
            :title-text="'Archive offer?'"
            :content-text="'Archiving this offer will revoke the customers access to the course. \n\n You will not be able to restore the offer once it has been archived. \n\n The offer will still be visible to you in your licence table. \n\n Would you like to archive this offer?'"
            :cancel-button-text="'Cancel'"
            :submit-button-text="'Yes'"
            :submit-button-danger="true"
            :submit-button-animate-loading="true"
            :prevent-close="true"
            @emit-submit="performArchiveLicence"
        >
        </pop-notify>
    </div>
</template>

<script>
import ViewActions from '@/components/default/shared/ViewActions'
import ComponentText from '@/components/default/shared/ComponentText'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import LicenceShowTable from '@/components/ebs/licences/show/LicenceShowTable'
import ComponentList from '@/components/default/shared/ComponentList'
import ComponentStatus from '@/components/default/shared/ComponentStatus'
import ComponentButton from '@/components/default/shared/ComponentButton'
import PopNotify from '@/components/default/shared/PopNotify'
import FormEditor from '@/components/default/forms/FormEditor'

export default {
  name: 'licence-show',
  components: {
    FormEditor,
    PopNotify,
    ComponentButton,
    ComponentStatus,
    ComponentList,
    ComponentWrapper,
    ComponentTitle,
    ComponentText,
    ViewActions
  },
  data () {
    return {
      LicenceShowTable
    }
  },
  computed: {
    licenceStatus () {
      return this.$store.getters.getEbsLicenceStatus
    },
    licenceId () {
      return this.$store.getters.getEbsLicenceId
    },
    licenceLatestStateHistoryDescription () {
      return this.$store.getters.getEbsLicenceLatestStateHistoryDescription
    },
    licenceDescription: {
      get () {
        return this.$store.getters.getEbsLicenceCustomDescription
      },
      set (value) {
        this.$store.dispatch('ebsLicenceSetCustomDescription', value)
      }
    },
    routeParamsContractId () {
      return this.$route.params.contractId
    },
    routeQueryCourseName () {
      return this.$route.query.courseName
    },
    routeQueryCustomerName () {
      return this.$route.query.customerName
    }
  },
  methods: {
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('licence-submit')
    },
    //
    licenceStopShow: function () {
      this.allowRedirectBeforeRouteLeave()
      this.$router.push({ name: 'ebs-licence-list' })
    },
    handleCollapse (value) {
      this.activeCollapseIndex = this.activeCollapseIndex !== value ? value : ''
    },
    handleGenerateLicenceConsumptionsCSV () {
      this.$store.dispatch('ebsLicenceConsumptionsGetCSV', {
        contractId: this.routeParamsContractId,
        courseName: this.routeQueryCourseName,
        customerName: this.routeQueryCustomerName
      }).finally(() => {
        this.$eventBus.$emit('stop-generate-licence-consumptions-csv')
      })
    },
    openNotifySuspendLicence (data) {
      this.licenceDescription = ''
      this.$refs.notifysuspendoffer.openNotify()
    },
    openNotifyRestoreLicence (data) {
      this.licenceDescription = ''
      this.$refs.notifyrestoreoffer.openNotify()
    },
    openNotifyArchiveLicence (data) {
      this.licenceDescription = ''
      this.$refs.notifyarchiveoffer.openNotify()
    },
    performSuspendLicence () {
      this.$store.dispatch('ebsLicenceSuspend', this.licenceId).then(() => {
        this.$router.push({ name: 'ebs-licence-list' })
      }).finally(() => {
        this.$refs.notifysuspendoffer.closeNotify()
      })
    },
    performRestoreLicence () {
      this.$store.dispatch('ebsLicenceRestore', this.licenceId).then(() => {
        this.$router.push({ name: 'ebs-licence-list' })
      }).finally(() => {
        this.$refs.notifyrestoreoffer.closeNotify()
      })
    },
    performArchiveLicence () {
      this.$store.dispatch('ebsLicenceArchive', this.licenceId).then(() => {
        this.$router.push({ name: 'ebs-licence-list' })
      }).finally(() => {
        this.$refs.notifyarchiveoffer.closeNotify()
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/licences/licenceshow";
</style>
